import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import CloseIcon from "assets/icons/close.svg?react";
import messagesService from "api/messagesService";
import { useEffect, useMemo, useState } from "react";
import { Evidence, IMetric, Receiver } from "dto/IMetric";
import moment from "moment";
import axios from "axios";
import { getUser } from "api/axios";
import Input from "components/Input";
import DownloadIcon from "assets/icons/download.svg?react";
import PdfIcon from "assets/icons/pdf.svg?react";
import {
  faChevronDown,
  faChevronUp,
  faCaretDown,
  faCaretUp,
} from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import Avatar from "components/Avatar";
import statusService from "api/statusService";
import OutgoingParcelReceiverRow from "components/OutgoingParcelReceiverRow";
import { ItemKind } from "dto/IKindItems";
import CourtPortalService from "api/CourtPortalService";
import { motion, AnimatePresence } from "framer-motion";
import { getItemKindName, getParcelTypeName } from "utils/getItemKindName";
import { randomString } from "utils/randomString";
import { ESendStatus } from "components/IconsState/typings";
import { EParcelType } from "dto/EParcelType";
import { EEvidenceEvent } from "dto/EEvidenceEvent";

const Accordion = ({ expanded, setExpanded, children }) => {
  // By using `AnimatePresence` to mount and unmount the contents, we can animate
  // them in and out while also only rendering the contents of open accordions
  return (
    <>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.section
            key="content"
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={{
              open: { opacity: 1, height: "auto" },
              collapsed: { opacity: 0, height: 0 },
            }}
          >
            {children}
          </motion.section>
        )}
      </AnimatePresence>
    </>
  );
};

const MetricUser = ({ receiver }: { receiver: Receiver }) => {
  const user = getUser();
  const token = user?.access_token;
  const [show, setShow] = useState(false);

  const handleDownload = async (e: Evidence) => {
    console.log("start downlload");
    const blob = await axios.get(
      `${import.meta.env.VITE_APP_API_URL}/message/evidence?id=${e.Id}`,
      {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    const URL = window.URL.createObjectURL(new Blob([blob.data]));
    const link = document.createElement("a");
    link.href = URL;
    link.setAttribute("download", "Potwierdzenie.pdf");
    document.body.appendChild(link);
    link.click();
  };

  const handleDownloadXml = async (e: Evidence) => {
    console.log("start downlload");
    const blob = await axios.get(
      `${import.meta.env.VITE_APP_API_URL}/message/evidencexml?id=${e.Id}`,
      {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    const URL = window.URL.createObjectURL(
      new Blob([blob.data], { type: "application/xml" }),
    );
    const link = document.createElement("a");
    link.href = URL;
    link.setAttribute("download", `${e.Id}.xml`);
    link.setAttribute;
    document.body.appendChild(link);
    link.click();
  };

  return (
    <div className={styles.metricUser}>
      <div onClick={() => setShow(!show)} className={styles.card}>
        <div>
          {receiver?.CompanyName ??
            `${receiver?.Name ?? ""} ${receiver?.Surname ?? ""}`}
          {!receiver.CompanyName &&
            !receiver.Name &&
            !receiver.Surname &&
            `${receiver.EDeliveryAddress}`}
        </div>
        <FontAwesomeIcon icon={show ? faChevronUp : faChevronDown} />
      </div>
      <div className={styles.evidences}>
        {show &&
          receiver?.Evidences?.map((evidence) => {
            return (
              <div key={evidence.Id} className={styles.evidence}>
                <div className={styles.information}>
                  <div className={styles.name}>{evidence.Label}</div>
                  <div className={styles.date}>
                    {moment(evidence.CreationDate).format("YYYY.MM.DD HH:mm")}
                  </div>
                </div>
                <div className={styles.buttons}>
                  {(evidence.Event === EEvidenceEvent.SubmissionAcceptance ||
                    evidence.Event === EEvidenceEvent.ContentHandover) && (
                    <div
                      onClick={() => handleDownload(evidence)}
                      className={styles.downloadWrapper}
                    >
                      <PdfIcon />
                    </div>
                  )}
                  <div
                    onClick={() => handleDownloadXml(evidence)}
                    className={styles.downloadWrapper}
                  >
                    <DownloadIcon />
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

const MetricModal: FC<Props> = ({ toggle, pack }) => {
  const { t } = useTranslation();
  const { data: metricData } = messagesService.useGetMetric(pack.Id, pack.Kind);
  const { mutate: getEpok } = CourtPortalService.useGetEpok();
  const metricD: IMetric = useMemo(() => metricData?.data ?? [], [metricData]);

  const [metric, setMetric] = useState<IMetric | null>(null);
  const [isHistoryExpanded, setIsHistoryExpanded] = useState<boolean>(true);
  const [isPassHistoryExpanded, setIsPassHistoryExpanded] =
    useState<boolean>(true);
  const [isEvidencesExpanded, setIsEvidencesExpanded] =
    useState<boolean>(false);

  const [isReceiversExpanded, setIsReceiversExpanded] =
    useState<boolean>(false);

  const [isCcReceiversExpanded, setIsCcReceiversExpanded] =
    useState<boolean>(false);

  useEffect(() => {
    setMetric(metricD);
  }, [metricD]);
  const { data: statusesData } = statusService.useGetStatuses();
  const statusList = useMemo(() => statusesData?.data ?? [], [statusesData]);
  const user = getUser();
  const token = user?.access_token;

  const handleDownloadAll = async () => {
    const blob = await axios.get(
      `${import.meta.env.VITE_APP_API_URL}/message/evidences?id=${pack.Id}`,
      {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    const URL = window.URL.createObjectURL(new Blob([blob.data]));
    const link = document.createElement("a");
    link.href = URL;
    link.setAttribute("download", `M-${pack.Id}.zip`);
    document.body.appendChild(link);
    link.click();
  };

  const handleDownloadEpok = () => {
    getEpok(
      { id: pack.Id },
      {
        onSuccess(data, variables, context) {
          if (data.data?.Url) {
            const newWindow = window.open(
              data.data.Url,
              "_blank",
              "noopener,noreferrer",
            );
            if (newWindow) newWindow.opener = null;
          }
        },
      },
    );
  };

  if (!metric) {
    return <></>;
  }

  const hasEvidences = !!metric?.Receivers?.map((r) => r.Evidences ?? []).flat()
    .length;

  const hasCc = pack.Receivers.find((r) => r.Cc || r.Bcc);

  const getDocumentNumber = () => {
    if (pack) {
      if (pack.Kind === ItemKind.CourtPortalDeliverance) return pack.S1;
      if (
        pack.Kind === ItemKind.OutgoingParcel &&
        pack.Receivers &&
        pack.Receivers.length &&
        pack.Receivers[0].ParcelType === EParcelType.NotaEncrypted
      )
        return pack.S4;

      return pack.Id;
    }
    return "";
  };

  return (
    <div className={styles.modal}>
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <div className={styles.title}>{t("metric.title")}</div>
          <CloseIcon
            onClick={() => {
              toggle(false);
            }}
            className={styles.close}
          />
        </div>
        <div className={styles.body}>
          <div className={styles.inputs}>
            <Input
              value={
                pack.Kind === ItemKind.OutgoingParcel &&
                pack.Receivers?.length > 0
                  ? getParcelTypeName(pack.Receivers[0].ParcelType)
                  : getItemKindName(pack.Kind)
              }
              label={t("common.type")}
              onChange={() => {}}
              disabled
              focus
            />
            {pack.Kind !== ItemKind.CourtPortalDeliverance && (
              <Input
                value={metric.Subject ?? ""}
                label={t("forms.title")}
                onChange={() => {}}
                disabled
                focus
              />
            )}
            <Input
              value={getDocumentNumber()}
              label={t("metric.parcelNumber")}
              onChange={() => {}}
              disabled
              focus
            />
            <Input
              value={
                metric?.Sender?.CompanyName ??
                `${metric?.Sender?.Name ?? ""} ${metric?.Sender?.Surname ?? ""}`
              }
              label={t("forms.sender")}
              onChange={() => {}}
              disabled
              focus
            />
            {metric.Sender?.EDeliveryAddress && (
              <Input
                value={metric?.Sender?.EDeliveryAddress}
                label={t("metric.senderADE")}
                onChange={() => {}}
                disabled
                focus
              />
            )}
            {metric?.Receivers != null &&
              metric.Receivers.length &&
              metric.ReceiveDate && (
                <Input
                  value={
                    metric.Receivers[0].CompanyName ??
                    `${metric.Receivers[0].Name ?? ""} ${
                      metric.Receivers[0].Surname ?? ""
                    }`
                  }
                  label={t("packages.receivedBy")}
                  onChange={() => {}}
                  disabled
                  focus
                />
              )}
          </div>

          <div className={styles.evidencesWrapper}>
            <div className={styles.evidenceHeader}>
              <div className={styles.evidenceTitle}>{t("metric.recipientsTo")}</div>
              <div
                onClick={(e) => {
                  setIsReceiversExpanded(!isReceiversExpanded);
                  e.stopPropagation();
                }}
                className={styles.expanderWrapper}
              >
                <FontAwesomeIcon
                  className={classNames(styles.expander)}
                  icon={isReceiversExpanded ? faCaretDown : faCaretUp}
                />
              </div>
            </div>
            <Accordion
              expanded={isReceiversExpanded}
              setExpanded={setIsReceiversExpanded}
            >
              <div className={styles.evidencesWrapper}>
                <div className={styles.list}>
                  {pack.Kind !== ItemKind.EDeliveryMessage &&
                    pack.Receivers.filter((r) => !r.Cc && !r.Bcc).map(
                      (r, index) => (
                        <>
                          <OutgoingParcelReceiverRow
                            key={index}
                            pack={pack}
                            receiver={r}
                            statuses={statusList}
                          />
                          {pack.Receivers.length - 1 > index && (
                            <div className={styles.divinder}></div>
                          )}
                        </>
                      ),
                    )}
                  {pack.Kind === ItemKind.EDeliveryMessage &&
                    metricD?.Receivers &&
                    metricD.Receivers.map((r, index) => (
                      <>
                        <OutgoingParcelReceiverRow
                          key={index}
                          pack={pack}
                          receiver={{
                            ...r,
                            ParcelType: EParcelType.EDelivery,
                            SendStatus: ESendStatus.SENT,
                            Bcc: false,
                            Cc: false,
                            NewestEvidenceEvent: metricD.NewestEvidenceEvent,
                          }}
                          statuses={statusList}
                        />
                        {pack.Receivers.length - 1 > index && (
                          <div className={styles.divinder}></div>
                        )}
                      </>
                    ))}
                </div>
              </div>
            </Accordion>
          </div>

          {hasCc && (
            <div className={styles.evidencesWrapper}>
              <div className={styles.evidenceHeader}>
                <div className={styles.evidenceTitle}>{t("metric.recipientsCc")}</div>
                <div
                  onClick={(e) => {
                    setIsCcReceiversExpanded(!isCcReceiversExpanded);
                    e.stopPropagation();
                  }}
                  className={styles.expanderWrapper}
                >
                  <FontAwesomeIcon
                    className={classNames(styles.expander)}
                    icon={isReceiversExpanded ? faCaretDown : faCaretUp}
                  />
                </div>
              </div>

              <Accordion
                expanded={isCcReceiversExpanded}
                setExpanded={setIsCcReceiversExpanded}
              >
                <div className={styles.evidencesWrapper}>
                  <div className={styles.list}>
                    {pack.Receivers.filter((r) => r.Cc || r.Bcc).map(
                      (r, index) => (
                        <>
                          <OutgoingParcelReceiverRow
                            pack={pack}
                            key={index}
                            receiver={r}
                            statuses={statusList}
                          />
                          {pack.Receivers.length - 1 > index && (
                            <div className={styles.divinder}></div>
                          )}
                        </>
                      ),
                    )}
                  </div>
                </div>
              </Accordion>
            </div>
          )}

          {hasEvidences && (
            <div className={styles.evidencesWrapper}>
              <div className={styles.evidenceHeader}>
                <div className={styles.evidenceTitle}>{t("metric.evidence")}</div>
                <div
                  onClick={(e) => {
                    setIsEvidencesExpanded(!isEvidencesExpanded);
                    e.stopPropagation();
                  }}
                  className={styles.expanderWrapper}
                >
                  <FontAwesomeIcon
                    className={classNames(styles.expander)}
                    icon={isEvidencesExpanded ? faCaretDown : faCaretUp}
                  />
                </div>
              </div>

              <Accordion
                expanded={isEvidencesExpanded}
                setExpanded={setIsEvidencesExpanded}
              >
                <div className={styles.evidencesWrapper}>
                  {metric?.Receivers?.map((receiver) => {
                    if (!receiver.Evidences) {
                      return <></>;
                    }

                    return <MetricUser receiver={receiver} />;
                  })}
                </div>
                <div onClick={handleDownloadAll} className={styles.download}>
                  {t("metric.downloadAll")}
                </div>
              </Accordion>
            </div>
          )}

          {metricD?.History && metricD.History.length > 0 && (
            <div className={styles.history}>
              <div className={styles.header}>
                {t("metric.history")}
                <div
                  onClick={(e) => {
                    setIsHistoryExpanded(!isHistoryExpanded);
                    e.stopPropagation();
                  }}
                  className={styles.expanderWrapper}
                >
                  <FontAwesomeIcon
                    className={classNames(styles.expander)}
                    icon={isHistoryExpanded ? faCaretDown : faCaretUp}
                  />
                </div>
              </div>
              <Accordion
                expanded={isHistoryExpanded}
                setExpanded={setIsHistoryExpanded}
              >
                <div className={styles.list}>
                  {metricD.History.map((h) => (
                    <div
                      className={styles.box}
                      key={`history_${h.Id ?? randomString()}`}
                    >
                      <div className={styles.boxWrapper}>
                        <div className={styles.line}></div>

                        {h.User && (
                          <Avatar
                            name={h.User}
                            className={styles.avatar}
                            size="small"
                          ></Avatar>
                        )}

                        {!h.User &&
                          pack.Kind === ItemKind.CourtPortalDeliverance && (
                            <Avatar
                              name={"Portal Informacyjny"}
                              className={styles.avatar}
                              size="small"
                            ></Avatar>
                          )}

                        <div className={styles.boxContent}>
                          <div className={styles.r1}>
                            <div className={styles.desc}>{h.Description}</div>
                            <div className={styles.date}>
                              {moment(h.Date).format("yyyy.MM.DD HH:mm")}
                            </div>
                          </div>
                          <div className={styles.r2}>
                            <div className={styles.desc}>
                              {/* {removeHtml(h.Comment)} */}
                              {h.User && `${t("packages.receivedBy")}: ${h.User}`}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Accordion>
            </div>
          )}

          {metricD?.HasEpok && (
            <div onClick={handleDownloadEpok} className={styles.download}>
              {t("metric.downloadEpok")}
            </div>
          )}

          {metricD?.PassHistory && metricD.PassHistory.length > 0 && (
            <div className={styles.history}>
              <div className={styles.header}>
                {t("metric.transferHistory")}
                <div
                  onClick={(e) => {
                    setIsPassHistoryExpanded(!isPassHistoryExpanded);
                    e.stopPropagation();
                  }}
                  className={styles.expanderWrapper}
                >
                  <FontAwesomeIcon
                    className={classNames(styles.expander)}
                    icon={isPassHistoryExpanded ? faCaretDown : faCaretUp}
                  />
                </div>
              </div>
              <Accordion
                expanded={isPassHistoryExpanded}
                setExpanded={setIsPassHistoryExpanded}
              >
                <div className={styles.list}>
                  {metricD.PassHistory.map((h) => (
                    <div className={styles.box}>
                      <div className={styles.boxWrapper}>
                        <div className={styles.line}></div>
                        <Avatar
                          name={h.From}
                          className={styles.avatar}
                          size="small"
                        ></Avatar>

                        <div className={styles.boxContent}>
                          <div className={styles.r1}>{h.From}</div>
                          <div className={styles.r2}>
                            <div className={styles.desc}>
                              {/* {removeHtml(h.Comment)} */}
                              {t("metric.transferTo")} {h.To}
                            </div>
                            <div className={styles.date}>
                              {moment(h.Date).format("yyyy.MM.DD HH:mm")}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Accordion>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MetricModal;
